import { format } from "date-fns";
import { useLayoutEffect, useRef, useState } from "react";
import { useLocation, useMatch, useNavigate } from "react-router-dom";
import { Form, Input } from "semantic-ui-react";
import DatePicker from "../components/datePicker/DatePicker";
import { CallView } from "../components/calls/CallView";
import CallRecordings from "../components/sidekick/CallRecordings";
import CallTranscriptions from "../components/sidekick/CallTranscriptions";
import SidekickMinutesBanner from "../components/sidekick/SidekickMinutesBanner";
import {
  Btn,
  Card,
  Field,
  Icon,
  Notice,
  Page,
  PageHeader,
  Tabs,
} from "../components/ui";
import config from "../config";
import sidekickLogoWhite from "../images/sidekick-logo-white-75x79.png";

const TABS = [
  { value: "search", label: "Search" },
  { value: "processed", label: "Processed" },
];

const style = {
  filters: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    alignItems: "flex-end",
  },
  filter: { flex: "1 1 170px", minWidth: 0 },
};

// Nearest scrolling ancestor (the app shell's main column)
function scrollParent(el) {
  for (let n = el?.parentElement; n; n = n.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(n).overflowY)) return n;
  }
  return document.scrollingElement;
}

// /sidekick/calls/:sqid shows the call in place of the lists. The lists stay
// mounted (hidden) so their tab, filters and page are as they were on Back.
function Sidekick() {
  const navigate = useNavigate();
  const location = useLocation();
  const openSqid = useMatch("/sidekick/calls/:sqid")?.params.sqid;
  const [currentTab, setCurrentTab] = useState(
    openSqid ? "processed" : "search",
  );

  const openCall = (sqid) =>
    navigate(`/sidekick/calls/${encodeURIComponent(sqid)}`, {
      state: { fromList: true },
    });
  const closeCall = () =>
    location.state?.fromList ? navigate(-1) : navigate("/sidekick");

  // Remember the list's scroll position while it's showing, start a call at
  // the top and put the list back where it was
  const rootRef = useRef(null);
  const listScroll = useRef(0);
  useLayoutEffect(() => {
    const el = scrollParent(rootRef.current);
    if (!el) return;
    if (openSqid) {
      el.scrollTop = 0;
      return;
    }
    el.scrollTop = listScroll.current;
    const onScroll = () => (listScroll.current = el.scrollTop);
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [openSqid]);

  const [searchCriteria, setSearchCriteria] = useState({});
  const [searchNumber, setSearchNumber] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchCriteria({
      tel_number: searchNumber,
      from: startDate ? format(startDate, "yyyy-MM-dd HH:mm:ss") : undefined,
      to: endDate ? format(endDate, "yyyy-MM-dd HH:mm:ss") : undefined,
    });
  };

  const resetSearch = () => {
    setSearchCriteria({});
    setSearchNumber("");
    setStartDate("");
    setEndDate("");
  };

  const hasCriteria = searchNumber || startDate || endDate;

  const filters = (
    <Card variant="panel" style={{ padding: "14px" }}>
      <Form onSubmit={handleSearch} style={style.filters}>
        <Field label="Number" style={style.filter}>
          <Input
            placeholder="Search by number"
            value={searchNumber}
            name="tel_number"
            onChange={(e) => setSearchNumber(e.target.value)}
          />
        </Field>
        <Field label="Start time" style={style.filter}>
          <DatePicker
            placeholderText="Start time"
            dateFormat="dd/MM/yyyy HH:mm"
            selected={startDate}
            showTimeSelect
            onChange={(date) => setStartDate(date)}
          />
        </Field>
        <Field label="End time" style={style.filter}>
          <DatePicker
            placeholderText="End time"
            selected={endDate}
            dateFormat="dd/MM/yyyy HH:mm"
            showTimeSelect
            onChange={(date) => setEndDate(date)}
          />
        </Field>
        <div style={{ display: "flex", gap: "8px" }}>
          <Btn
            type="submit"
            variant="primary"
            icon="search"
            loading={isSearching}
            size="lg"
          >
            Search
          </Btn>
          {hasCriteria && (
            <Btn
              icon="close"
              aria-label="Clear search"
              title="Clear search"
              size="lg"
              onClick={resetSearch}
            />
          )}
        </div>
      </Form>
    </Card>
  );

  return (
    <div ref={rootRef}>
      {openSqid && (
        <CallView key={openSqid} sqid={openSqid} onBack={closeCall} />
      )}
      <div hidden={!!openSqid}>
        <Page>
          <PageHeader
            title="Sidekick"
            actions={
              <Tabs
                ariaLabel="Sidekick"
                value={currentTab}
                onChange={setCurrentTab}
                items={TABS}
              />
            }
          />

          <Notice
            title="There's more in the Sidekick app"
            style={{ alignItems: "center", padding: "14px 16px" }}
            action={
              <Btn
                as="a"
                href={config.sidekick_url}
                target="_blank"
                rel="noopener noreferrer"
                variant="gradient"
                style={{ flexShrink: 0 }}
              >
                <img
                  src={sidekickLogoWhite}
                  alt=""
                  style={{ height: 14, width: "auto" }}
                />
                Open Sidekick
                <Icon name="externalLink" size={12} style={{ opacity: 0.75 }} />
              </Btn>
            }
          >
            Your meetings, notes and search are all there. This page just shows
            your iPECS calls.
          </Notice>

          <SidekickMinutesBanner />

          {/* One set of filters for both tabs */}
          {filters}

          {currentTab === "search" ? (
            <CallRecordings
              openCall={openCall}
              searchParams={searchCriteria}
              setIsSearching={setIsSearching}
              onViewProcessed={() => setCurrentTab("processed")}
            />
          ) : (
            <CallTranscriptions searchParams={searchCriteria} />
          )}
        </Page>
      </div>
    </div>
  );
}

export default Sidekick;
